/**
 * ListExecuteProgressOverlay — full-screen loading overlay for list execution.
 *
 * Renders the step label + percentage streamed via SSE while the synchronous
 * JSON execute request runs. Falls back to the plain spinner until the first
 * SSE event arrives, keeping the pre-SSE loading experience intact.
 */

import { MainLogo } from "modules/main/assets/main_logo";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import { ProgressState, ProgressStep } from "modules/lists/types/lists";

/** User-facing copy for each pipeline step (task 4.3 labels). */
const STEP_LABELS: Record<ProgressStep, string> = {
    starting: "Preparando...",
    embedding: "Analizando lista...",
    intent_search: "Buscando productos...",
    llm_batch: "Validando opciones...",
    completed: "Completado",
    failed: "No se pudo completar",
};

/** User-facing detail copy. Backend messages are transport metadata, not UI copy. */
const getStepMessage = (progress: ProgressState): string => {
    if (progress.step === "llm_batch" && progress.batchInfo) {
        return `Validando grupo ${progress.batchInfo}`;
    }

    const messages: Record<ProgressStep, string> = {
        starting: "Estamos preparando tu cotización.",
        embedding: "Estamos analizando los productos de tu lista.",
        intent_search: "Estamos buscando las mejores opciones disponibles.",
        llm_batch: "Estamos validando las alternativas encontradas.",
        completed: "Cotización completada.",
        failed: "No pudimos completar la cotización. Inténtalo nuevamente.",
    };

    return messages[progress.step];
};

interface ListExecuteProgressOverlayProps {
    isExecuting: boolean;
    progress: ProgressState | null;
}

export const ListExecuteProgressOverlay = ({ isExecuting, progress }: ListExecuteProgressOverlayProps) => {
    if (!isExecuting) return null;

    return (
        <div
            role="status"
            aria-live="polite"
            data-testid="execute-progress-overlay"
            className="fixed inset-0 z-50 bg-white/70 backdrop-blur-md flex flex-col items-center justify-center gap-4"
        >
            <div className="animate-pulse">
                <MainLogo classname="w-20" />
            </div>
            {progress ? (
                <div className="flex flex-col items-center gap-2 w-64">
                    <p className="text-sm font-semibold">{STEP_LABELS[progress.step]}</p>
                    <Progress value={progress.percentage} className="w-full" />
                    <p className="text-xs text-muted-foreground">{progress.percentage}%</p>
                    <p className="text-xs text-muted-foreground text-center">{getStepMessage(progress)}</p>
                </div>
            ) : (
                <Spinner className="size-5 text-muted-foreground" />
            )}
        </div>
    );
};
